import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Loading from '../../components/loading/Loading';

/**
 * OAuth Callback Component
 * Handles the redirect from Google OAuth
 * This page receives the token from the backend and completes the login
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const storageKey = 'googleOAuthResult';

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const userString = searchParams.get('user');
      const error = searchParams.get('error');

      // If this is a popup window, send message to parent
      if (window.opener && !window.opener.closed) {
        if (error) {
          localStorage.setItem(storageKey, JSON.stringify({
            type: 'GOOGLE_OAUTH_ERROR',
            error,
            timestamp: Date.now()
          }));
          window.opener.postMessage(
            {
              type: 'GOOGLE_OAUTH_ERROR',
              error: error
            },
            window.location.origin
          );
          setTimeout(() => window.close(), 100);
          return;
        }

        if (token && userString) {
          try {
            // Decode the user string properly
            const decodedUserString = decodeURIComponent(userString);
            const user = JSON.parse(decodedUserString);
            
            // Send success message to parent window
            const message = {
              type: 'GOOGLE_OAUTH_SUCCESS',
              token: token,
              user: JSON.stringify({
                ...user,
                SwitchUser: {
                  Email: user.Email,
                  ID: user.ID,
                  Name: user.Name,
                  Role: user.Role,
                  Username: user.Username || ''
                }
              })
            };

            localStorage.setItem(storageKey, JSON.stringify({
              type: 'GOOGLE_OAUTH_SUCCESS',
              token: token,
              user: message.user,
              timestamp: Date.now()
            }));
            
            window.opener.postMessage(message, window.location.origin);
            
            // Close the popup after a short delay
            setTimeout(() => {
              window.close();
              window.close();
            }, 100);
          } catch (err) {
            localStorage.setItem(storageKey, JSON.stringify({
              type: 'GOOGLE_OAUTH_ERROR',
              error: 'Failed to parse user data: ' + err.message,
              timestamp: Date.now()
            }));
            window.opener.postMessage(
              {
                type: 'GOOGLE_OAUTH_ERROR',
                error: 'Failed to parse user data: ' + err.message
              },
              window.location.origin
            );
            setTimeout(() => window.close(), 100);
          }
        }
      } else {
        // If not in popup (direct navigation), handle it here
        if (error) {
          localStorage.setItem(storageKey, JSON.stringify({
            type: 'GOOGLE_OAUTH_ERROR',
            error,
            timestamp: Date.now()
          }));
          navigate('/login', { state: { error: 'Authentication failed. Please try again.' } });
          return;
        }

        if (token && userString) {
          try {
            const decodedUserString = decodeURIComponent(userString);
            const user = JSON.parse(decodedUserString);
            login(token, JSON.stringify({
              ...user,
              SwitchUser: {
                Email: user.Email,
                ID: user.ID,
                Name: user.Name,
                Role: user.Role,
                Username: user.Username || ''
              }
            }));

            localStorage.setItem(storageKey, JSON.stringify({
              type: 'GOOGLE_OAUTH_SUCCESS',
              token: token,
              user: JSON.stringify({
                ...user,
                SwitchUser: {
                  Email: user.Email,
                  ID: user.ID,
                  Name: user.Name,
                  Role: user.Role,
                  Username: user.Username || ''
                }
              }),
              timestamp: Date.now()
            }));
          } catch (err) {
            localStorage.setItem(storageKey, JSON.stringify({
              type: 'GOOGLE_OAUTH_ERROR',
              error: 'Authentication failed. Please try again.',
              timestamp: Date.now()
            }));
            navigate('/login', { state: { error: 'Authentication failed. Please try again.' } });
          }
        } else {
          localStorage.setItem(storageKey, JSON.stringify({
            type: 'GOOGLE_OAUTH_ERROR',
            error: 'Authentication failed. Please try again.',
            timestamp: Date.now()
          }));
          navigate('/login', { state: { error: 'Authentication failed. Please try again.' } });
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '20px'
    }}>
      <Loading state={true} />
      <p style={{ color: '#6b7280', fontSize: '16px' }}>
        Completing authentication...
      </p>
    </div>
  );
};

export default OAuthCallback;
